"use-strict";

const PCBuilderUtils = (() => {
  const moneyRoot = document.querySelector("[data-pc-money-format]");
  const moneyFormat = moneyRoot?.getAttribute("data-pc-money-format");
  const currencyCode = moneyRoot?.getAttribute("data-pc-currency");

  const formatMoney = (value) => {
    let formatted = "";
    if (window.Shopify && typeof Shopify.formatMoney === "function") {
      formatted = Shopify.formatMoney(
        value,
        moneyFormat || window.Shopify.money_format
      );
    } else {
      const number = (value || 0) / 100;
      const code = currencyCode || "USD";
      formatted = new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: code,
        currencyDisplay: currencyCode ? "code" : "symbol",
      }).format(number);
    }
    if (currencyCode && !formatted.includes(currencyCode)) {
      formatted = `${formatted} ${currencyCode}`;
    }
    return formatted;
  };

  const base64Encode = (value) => {
    try {
      return btoa(unescape(encodeURIComponent(value)));
    } catch (e) {
      return "";
    }
  };

  const base64Decode = (value) => {
    try {
      return decodeURIComponent(escape(atob(value)));
    } catch (e) {
      return null;
    }
  };

  const confirmBundleAction = (options = {}) => {
    const normalized =
      typeof options === "string" ? { message: options } : (options || {});
    const bundleLabel = normalized.bundleLabel || "this bundle";
    const title = normalized.title || "This item is part of a custom bundle";
    const message =
      normalized.message ||
      `Items inside ${bundleLabel} can’t be removed individually.`;
    const editLabel = normalized.editLabel || "Edit bundle";
    const deleteLabel = normalized.deleteLabel || "Delete bundle";

    return new Promise((resolve) => {
      if (!document.getElementById("pc-builder-confirm-style")) {
        const style = document.createElement("style");
        style.id = "pc-builder-confirm-style";
        style.textContent = `
          .pc-builder__confirm-backdrop{position:fixed;inset:0;background:rgba(15,23,42,.38);display:flex;align-items:center;justify-content:center;padding:16px;z-index:9999;}
          .pc-builder__confirm{background:#fff;border-radius:12px;padding:18px;max-width:420px;width:min(92vw,420px);box-shadow:0 16px 50px rgba(0,0,0,.12);border:1px solid #e2e8f0;}
          .pc-builder__confirm-title{margin:0 0 8px;font-weight:900;color:#0f172a;line-height:1.3;font-size:16px;}
          .pc-builder__confirm-text{margin:0 0 10px;font-weight:650;color:#0f172a;line-height:1.4;}
          .pc-builder__confirm-list{margin:0 0 14px;padding-left:18px;color:#334155;line-height:1.45;font-size:13px;}
          .pc-builder__confirm-list li{margin:6px 0;}
          .pc-builder__confirm-actions{display:flex;justify-content:flex-end;gap:8px;}
          .pc-builder__confirm-actions .pc-builder__btn{display:inline-flex;align-items:center;justify-content:center;gap:8px;padding:10px 14px;border-radius:8px;font-weight:700;border:1px solid #e2e8f0;background:#fff;color:#0f172a;cursor:pointer;}
          .pc-builder__confirm-actions .pc-builder__btn--primary{background:#e63946;border-color:#e63946;color:#fff;}
          .pc-builder__confirm-actions .pc-builder__btn--primary:hover{color:#0f172a;}
          .pc-builder__confirm-actions .pc-builder__btn--ghost{background:#fff;border-color:#e2e8f0;}
          .pc-builder__confirm-actions .pc-builder__btn:hover{opacity:.92;}
          @media(max-width:540px){.pc-builder__confirm-actions{flex-direction:column;align-items:stretch;}.pc-builder__confirm-actions .pc-builder__btn{width:100%;}}
        `;
        document.head.appendChild(style);
      }

      const existing = document.querySelector(".pc-builder__confirm-backdrop");
      if (existing) existing.remove();

      const backdrop = document.createElement("div");
      backdrop.className = "pc-builder__confirm-backdrop";
      const modal = document.createElement("div");
      modal.className = "pc-builder__confirm";
      modal.setAttribute("role", "dialog");
      modal.setAttribute("aria-modal", "true");
      modal.innerHTML = `
        <h3 class="pc-builder__confirm-title">${title}</h3>
        <p class="pc-builder__confirm-text">${message}</p>
        <ul class="pc-builder__confirm-list">
          <li><strong>${editLabel}</strong> to change components and pricing.</li>
          <li><strong>${deleteLabel}</strong> to remove all items in the bundle.</li>
        </ul>
        <div class="pc-builder__confirm-actions">
          <button type="button" class="pc-builder__btn pc-builder__btn--ghost" data-action="edit">${editLabel}</button>
          <button type="button" class="pc-builder__btn pc-builder__btn--primary" data-action="delete">${deleteLabel}</button>
        </div>
      `;

      const cleanup = (result) => {
        backdrop.remove();
        resolve(result);
      };

      modal.addEventListener("click", (event) => {
        const action = event.target?.dataset?.action;
        if (action === "edit") cleanup("edit");
        if (action === "delete") cleanup("delete");
      });

      backdrop.addEventListener("click", (event) => {
        if (event.target === backdrop) cleanup(null);
      });

      document.addEventListener(
        "keydown",
        (event) => {
          if (event.key === "Escape") cleanup(null);
        },
        { once: true }
      );

      backdrop.appendChild(modal);
      document.body.appendChild(backdrop);
      modal.querySelector('[data-action="edit"]')?.focus();
    });
  };

  return { formatMoney, base64Encode, base64Decode, confirmBundleAction };
})();

const PC_BUILDER_DISCOUNT_VARIANTS = new Set([
  47319216685274,
  47319216947418,
]);

const PC_BUILDER_COLLECTION_PRODUCT_IDS = (() => {
  if (typeof window === "undefined") return new Set();
  const raw = window.PC_BUILDER_COLLECTION_PRODUCT_IDS;
  if (!Array.isArray(raw)) return new Set();
  return new Set(
    raw
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value) && value > 0)
  );
})();

const isPCBuilderDiscountItem = (item) => {
  if (!item) return false;
  const flag = item.properties?._pc_builder_discount;
  if (flag && flag !== "0") return true;
  const variantId = Number(item.variant_id || item.id || 0);
  return PC_BUILDER_DISCOUNT_VARIANTS.has(variantId);
};

const isPCBuilderCollectionItem = (item) => {
  if (!item) return false;
  if (!PC_BUILDER_COLLECTION_PRODUCT_IDS.size) return false;
  const productId = Number(item.product_id || 0);
  if (!productId) return false;
  return PC_BUILDER_COLLECTION_PRODUCT_IDS.has(productId);
};

const PC_BUILDER_DISCOUNT_STORAGE_KEY = "pc_builder_discount_variants";

const pcBuilderDiscountsEnabled = () =>
  typeof window !== "undefined" &&
  window.PC_BUILDER_DISCOUNTS_ENABLED === true;

const normalizePCBuilderVariantId = (value) => {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
};

const loadPCBuilderDiscountVariants = () => {
  try {
    return JSON.parse(
      localStorage.getItem(PC_BUILDER_DISCOUNT_STORAGE_KEY) || "{}"
    );
  } catch (e) {
    return {};
  }
};

const savePCBuilderDiscountVariants = (data) => {
  try {
    localStorage.setItem(
      PC_BUILDER_DISCOUNT_STORAGE_KEY,
      JSON.stringify(data || {})
    );
  } catch (e) {
    /* ignore */
  }
};

const getPCBuilderDiscountActions = (cart = {}) => {
  const updates = {};
  const additions = [];
  const cartItems = cart.items || [];

  if (!pcBuilderDiscountsEnabled()) {
    cartItems.forEach((item) => {
      if (isPCBuilderDiscountItem(item) && item.key) {
        updates[item.key] = 0;
      }
    });
    savePCBuilderDiscountVariants({});
    return { updates, additions };
  }

  const bundles = new Map();
  const discountItemsByBundle = new Map();
  const discountVariantsByBundle = new Map();
  let hasNonBundlePcBuilderItems = false;

  cartItems.forEach((item) => {
    if (isPCBuilderDiscountItem(item)) {
      const bundleId = item?.properties?._bundle_id || "";
      if (!discountItemsByBundle.has(bundleId)) {
        discountItemsByBundle.set(bundleId, []);
      }
      discountItemsByBundle.get(bundleId).push(item);
      const variantId = normalizePCBuilderVariantId(item.variant_id || item.id);
      if (bundleId && variantId) {
        discountVariantsByBundle.set(bundleId, variantId);
      }
      return;
    }

    const bundleId = item?.properties?._bundle_id;
    if (!bundleId) {
      if (isPCBuilderCollectionItem(item)) {
        hasNonBundlePcBuilderItems = true;
      }
      return;
    }

    const bundleNumber =
      item.properties?._bundle_number || item.properties?.["Bundle #"] || "";
    const builderName =
      item.properties?._builder_name ||
      item.properties?.["Bundle name"] ||
      "PC Builder bundle";
    const builderUrl = item.properties?._builder_url || "";
    const discountVariantId = normalizePCBuilderVariantId(
      item.properties?._pc_builder_discount_variant
    );
    const discountEnabledProperty =
      item.properties?._pc_builder_discount_enabled;
    const discountEnabled =
      discountEnabledProperty == null || discountEnabledProperty === ""
        ? null
        : !["0", "false"].includes(
            String(discountEnabledProperty).toLowerCase()
          );

    if (!bundles.has(bundleId)) {
      bundles.set(bundleId, {
        id: bundleId,
        number: bundleNumber,
        name: builderName,
        url: builderUrl,
        discountVariantId,
        discountEnabled,
      });
      return;
    }

    const existing = bundles.get(bundleId);
    if (!existing.number && bundleNumber) existing.number = bundleNumber;
    if (!existing.name && builderName) existing.name = builderName;
    if (!existing.url && builderUrl) existing.url = builderUrl;
    if (!existing.discountVariantId && discountVariantId) {
      existing.discountVariantId = discountVariantId;
    }
    if (discountEnabled === false) {
      existing.discountEnabled = false;
    } else if (existing.discountEnabled == null && discountEnabled != null) {
      existing.discountEnabled = discountEnabled;
    }
  });

  const storedVariants = loadPCBuilderDiscountVariants();
  const activeBundleIds = new Set();

  bundles.forEach((info, bundleId) => {
    activeBundleIds.add(bundleId);
    const knownVariant =
      info.discountVariantId || discountVariantsByBundle.get(bundleId);
    if (knownVariant) {
      storedVariants[bundleId] = knownVariant;
    }
  });

  Object.keys(storedVariants).forEach((bundleId) => {
    if (!activeBundleIds.has(bundleId)) delete storedVariants[bundleId];
  });
  savePCBuilderDiscountVariants(storedVariants);

  const hasBundles = bundles.size > 0;

  if (hasNonBundlePcBuilderItems && hasBundles) {
    discountItemsByBundle.forEach((items) => {
      items.forEach((item) => {
        updates[item.key] = 0;
      });
    });
    return { updates, additions };
  }

  discountItemsByBundle.forEach((items, bundleId) => {
    if (
      !bundleId ||
      !bundles.has(bundleId) ||
      bundles.get(bundleId).discountEnabled === false
    ) {
      items.forEach((item) => {
        updates[item.key] = 0;
      });
      return;
    }
    const [keep, ...rest] = items;
    if (keep && keep.quantity !== 1) {
      updates[keep.key] = 1;
    }
    rest.forEach((item) => {
      updates[item.key] = 0;
    });
  });

  bundles.forEach((info, bundleId) => {
    if (info.discountEnabled === false) return;
    if (discountItemsByBundle.has(bundleId)) return;
    const variantId =
      info.discountVariantId || storedVariants[bundleId] || 0;
    if (!variantId) return;
    additions.push({
      id: variantId,
      quantity: 1,
      properties: {
        _bundle_id: bundleId,
        _bundle_number: info.number || "",
        _pc_builder: "1",
        _pc_builder_discount: "1",
        _pc_builder_discount_variant: String(variantId),
        _builder_name: info.name || "PC Builder bundle",
        _builder_url: info.url || "",
        "Bundle name": info.name || "PC Builder bundle",
      },
    });
  });

  return { updates, additions };
};

const PCBuilderDiscountGuard = {
  running: false,
  scheduled: false,
  async sync() {
    if (this.running) return null;
    this.running = true;
    try {
      const cartRes = await fetch("/cart.js");
      if (!cartRes.ok) return null;
      const cart = await cartRes.json();
      const { updates, additions } = getPCBuilderDiscountActions(cart);
      const hasUpdates = Object.keys(updates).length > 0;
      const hasAdditions = additions.length > 0;
      if (!hasUpdates && !hasAdditions) return null;

      const sections = [];
      const mainCart = document.getElementById("main-cart-items");
      if (mainCart?.dataset?.id) sections.push(mainCart.dataset.id);
      if (document.getElementById("minicart-form")) {
        sections.push("minicart-form");
      }
      const sectionsPayload = sections.length ? sections : undefined;
      const updateUrl = window.routes?.cart_update_url || "/cart/update.js";
      let state = null;

      if (hasUpdates) {
        const body = JSON.stringify({
          updates,
          sections: sectionsPayload,
          sections_url: window.location.pathname,
        });
        const res = await fetch(updateUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body,
          __pcBuilderGuard: true,
        });
        state = JSON.parse(await res.text());
      }

      if (hasAdditions) {
        const baseUrl = window.routes?.cart_add_url || "/cart/add";
        const addUrl = baseUrl.endsWith(".js") ? baseUrl : `${baseUrl}.js`;
        const body = JSON.stringify({
          items: additions,
          sections: sectionsPayload,
          sections_url: window.location.pathname,
        });
        const res = await fetch(addUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body,
          __pcBuilderGuard: true,
        });
        const addState = JSON.parse(await res.text());
        const refreshed = await fetch("/cart.js").then((r) =>
          r.ok ? r.json() : null
        );
        if (refreshed) {
          state = { ...refreshed, sections: addState.sections || {} };
        }
      }

      if (!state) return null;
      if (
        typeof CartUtils !== "undefined" &&
        typeof CartUtils.updateCartUI === "function"
      ) {
        const cartItems = document.querySelector("cart-items");
        if (cartItems && cartItems.getSectionsToRender) {
          CartUtils.updateCartUI({
            parsedState: state,
            sectionsToRender: cartItems.getSectionsToRender(),
            cartInstance: cartItems,
            totalsSelector: ".cart-info .totals",
          });
        }
      }
      const cartNotification = document.querySelector("cart-notification");
      if (
        cartNotification &&
        typeof cartNotification.applyPcBuilderDiscountState === "function"
      ) {
        cartNotification.applyPcBuilderDiscountState(state);
      }
      return state;
    } catch (e) {
      console.warn("[PC Builder] Discount guard failed", e);
      return null;
    } finally {
      this.running = false;
    }
  },
  schedule() {
    if (this.scheduled) return;
    this.scheduled = true;
    requestAnimationFrame(() => {
      this.scheduled = false;
      this.sync();
    });
  },
};

(() => {
  if (window.__pcBuilderFetchPatched) return;
  window.__pcBuilderFetchPatched = true;
  const originalFetch = window.fetch;
  window.fetch = (input, init) => {
    const url =
      typeof input === "string"
        ? input
        : input?.url || "";
    const isGuard = Boolean(init && init.__pcBuilderGuard);
    let requestInit = init;
    if (init && init.__pcBuilderGuard) {
      requestInit = { ...init };
      delete requestInit.__pcBuilderGuard;
    }
    const isMutation = /\/cart\/(add|change|update|clear)(\.js|$)/.test(url);
    const requestPromise =
      requestInit === undefined
        ? originalFetch(input)
        : originalFetch(input, requestInit);
    return requestPromise.then((response) => {
      if (isMutation && !isGuard) {
        PCBuilderDiscountGuard.schedule();
      }
      return response;
    });
  };
})();

class PCBuilder {
  constructor(root) {
    this.root = root;
    this.config = this.parseConfig();
    if (!this.config || !this.config.categories.length) return;
    this.builderKey = this.root.getAttribute("data-builder-key") || "pc-builder";
    this.state = {
      activeCategory: this.config.categories[0].id,
      selections: {},
    };
    this.variantLookup = this.buildVariantLookup();
    this.moneyFormat = this.config.moneyFormat;
    this.messageEl = this.root.querySelector("[data-pc-builder-message]");
    this.summaryList = this.root.querySelector("[data-pc-summary-list]");
    this.priceNodes = {
      subtotal: this.root.querySelector("[data-pc-subtotal]"),
      discount: this.root.querySelector("[data-pc-discount]"),
      final: this.root.querySelector("[data-pc-final]"),
      discountBadge: this.root.querySelector("[data-pc-discount-badge]"),
      selectedCount: this.root.querySelector("[data-pc-selected-count]"),
    };
    this.buttons = {
      addToCart: this.root.querySelector("[data-pc-add-cart]"),
      share: this.root.querySelector("[data-pc-share]"),
      summaryOpen: this.root.querySelector("[data-pc-open-summary]"),
    };
    this.modal = this.root.querySelector("[data-pc-summary-modal]");
    this.modalClose = this.root.querySelector("[data-pc-summary-close]");
    this.shareInput = this.root.querySelector("[data-pc-share-input]");
    this.builderStatus = this.root.querySelector("[data-pc-builder-status]");
    this.builderDiscountBadges = Array.from(
      this.root.querySelectorAll("[data-pc-builder-discount]")
    );
    this.modalPrice = {
      subtotal: this.root.querySelector("[data-pc-modal-subtotal]"),
      final: this.root.querySelector("[data-pc-modal-final]"),
      discountBadge: this.root.querySelector("[data-pc-modal-discount-badge]"),
      selectedCount: this.root.querySelector("[data-pc-modal-selected-count]"),
    };
    this.summaryGrid = this.root.querySelector("[data-pc-summary-grid]");
    const urlParams = new URLSearchParams(window.location.search);
    this.isEditing = Boolean(urlParams.get("edit"));
    this.bundleId = urlParams.get("edit") || this.generateBundleId();
    this.bundleNumber = this.generateBundleNumber(this.bundleId);
    this.bindEvents();
    if (!this.isEditing) {
      this.hydrateFromStorage();
    }
    this.hydrateFromUrl();
    if (!this.isEditing) {
      this.applyDefaultSelections();
    }
    this.renderFromState();
    this.updateShareLink();
    this.refreshPreviewAvailability();
  }

  parseConfig() {
    const jsonEl = this.root.querySelector("[data-pc-builder-config]");
    if (!jsonEl) return null;
    try {
      const payload = JSON.parse(jsonEl.textContent || "{}");
      return payload;
    } catch (e) {
      console.error("Invalid PC Builder config", e);
      return null;
    }
  }

  buildVariantLookup() {
    const map = new Map();
    this.config.categories.forEach((cat) => {
      cat.products.forEach((p) => {
        map.set(String(p.variant_id), { categoryId: cat.id, product: p });
      });
    });
    return map;
  }

  generateBundleId() {
    return (
      window.crypto?.randomUUID?.() ||
      `pcbuilder-${Date.now()}-${Math.floor(Math.random() * 1e4)}`
    );
  }

  generateBundleNumber(id) {
    if (!id) return "";
    const hash = Array.from(id).reduce(
      (acc, char) => (acc * 31 + char.charCodeAt(0)) >>> 0,
      7
    );
    return `#${String(hash % 1000000).padStart(6, "0")}`;
  }

  resetBundleIdentity() {
    this.bundleId = this.generateBundleId();
    this.bundleNumber = this.generateBundleNumber(this.bundleId);
    this.persistState();
    this.updateShareLink();
  }

  bindEvents() {
    this.root.querySelectorAll("[data-pc-nav]").forEach((nav) => {
      nav.addEventListener("click", () => {
        const target = nav.getAttribute("data-pc-nav");
        this.setActiveCategory(target);
      });
    });

    this.root.querySelectorAll("[data-pc-category]").forEach((categoryEl) => {
      const categoryId = categoryEl.getAttribute("data-pc-category");
      categoryEl
        .querySelectorAll("[data-pc-card]")
        .forEach((card) => this.bindCard(card, categoryId));
      const nextBtn = categoryEl.querySelector("[data-pc-next]");
      const prevBtn = categoryEl.querySelector("[data-pc-prev]");
      nextBtn?.addEventListener("click", () => this.goNext(categoryId));
      prevBtn?.addEventListener("click", () => this.goPrev(categoryId));
    });

    this.buttons.addToCart?.addEventListener("click", () =>
      this.handleAddToCart()
    );

    this.buttons.share?.addEventListener("click", () =>
      this.copyShareLink()
    );

    this.buttons.summaryOpen?.addEventListener("click", () =>
      this.toggleModal(true)
    );
    this.modalClose?.addEventListener("click", () => this.toggleModal(false));
    this.modal?.addEventListener("click", (e) => {
      if (e.target === this.modal) this.toggleModal(false);
    });
    this.root.querySelector("[data-pc-summary-grid]")?.addEventListener("click", (e) => {
      const editBtn = e.target.closest("[data-pc-row-edit]");
      const addBtn = e.target.closest("[data-pc-row-add-cart]");
      if (editBtn) {
        const catId = editBtn.getAttribute("data-cat-id");
        this.setActiveCategory(catId);
        this.toggleModal(false);
      }
      if (addBtn) {
        this.handleAddToCart();
      }
    });
    this.root.querySelector("[data-pc-continue]")?.addEventListener("click", () => {
      this.toggleModal(false);
    });
    this.root.querySelector("[data-pc-modal-add]")?.addEventListener("click", () => {
      this.handleAddToCart();
    });
  }

  bindCard(card, categoryId) {
    const categoryEl = card.closest("[data-pc-category]");
    const catType = categoryEl.dataset.categoryType;
    const isMulti = ["required_multi", "optional_multi"].includes(catType);
    const variantId = card.getAttribute("data-variant-id");
    const productId = card.getAttribute("data-product-id");
    const title = card.getAttribute("data-title");
    const sku = card.getAttribute("data-sku") || "";
    const price = Number(card.getAttribute("data-price")) || 0;
    const compare = Number(card.getAttribute("data-compare")) || 0;
    const availableAttr = card.getAttribute("data-available");
    const available = availableAttr !== "false";
    const image = card.getAttribute("data-image") || "";
    const inventoryQtyRaw = card.getAttribute("data-inventory");
    const inventoryQty =
      inventoryQtyRaw === null || inventoryQtyRaw === ""
        ? null
        : Number(inventoryQtyRaw);
    const inventoryPolicy = card.getAttribute("data-inventory-policy") || "";
    const inventoryManaged = card.getAttribute("data-inventory-managed") || "";
    const maxQty = Number(categoryEl.dataset.maxQuantity || "1");

    if (!available) {
      card.classList.add("is-disabled");
    } else {
      card.querySelector(".pc-builder-card__stock")?.remove();
    }

    card.addEventListener("click", (e) => {
      const qtyBtn = e.target.closest("[data-pc-qty-btn]");
      if (qtyBtn) return;
      if (!available) return;
      const category = this.getCategory(categoryId);
      const isOptional = category && !category.required;
      const currentQty =
        this.state.selections[categoryId]?.items?.[variantId]?.quantity || 0;
      if (isMulti) {
        if (currentQty > 0) {
          const totalQty = this.getCategoryTotal(categoryId);
          const remaining = totalQty - currentQty;
          if (!isOptional && remaining <= 0) return;
          this.adjustQuantity(categoryId, variantId, {
            productId,
            title,
            sku,
            price,
            compare,
            available,
            image,
            inventoryQty,
            inventoryPolicy,
            inventoryManaged,
            delta: -currentQty,
          });
          return;
        }
        this.adjustQuantity(categoryId, variantId, {
          productId,
          title,
          sku,
          price,
          compare,
          available,
          image,
          inventoryQty,
          inventoryPolicy,
          inventoryManaged,
          delta: 1,
        });
      } else {
        if (isOptional && currentQty > 0) {
          delete this.state.selections[categoryId].items[variantId];
          if (
            Object.keys(this.state.selections[categoryId].items || {}).length ===
            0
          ) {
            delete this.state.selections[categoryId];
          }
          this.renderFromState();
          return;
        }
        this.setSingleSelection(categoryId, {
          variantId,
          productId,
          title,
          sku,
          price,
          compare,
          available,
          image,
          inventoryQty,
          inventoryPolicy,
          inventoryManaged,
        });
      }
    });

    card.querySelectorAll("[data-pc-qty-btn]").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        if (!available) return;
        const direction = btn.dataset.pcQtyBtn === "plus" ? 1 : -1;
        this.adjustQuantity(categoryId, variantId, {
          productId,
          title,
          sku,
          price,
          compare,
          available,
          image,
          inventoryQty,
          inventoryPolicy,
          inventoryManaged,
          delta: direction,
          maxQty,
        });
      });
    });
  }

  setActiveCategory(id) {
    if (!id) return;
    this.state.activeCategory = id;
    this.root.querySelectorAll("[data-pc-category]").forEach((cat) => {
      cat.classList.toggle(
        "is-active",
        cat.getAttribute("data-pc-category") === id
      );
    });
    this.root.querySelectorAll("[data-pc-nav]").forEach((nav) => {
      nav.classList.toggle("is-active", nav.getAttribute("data-pc-nav") === id);
    });
    this.updateNextButtons();
  }

  goNext(currentId) {
    const ids = this.config.categories.map((c) => c.id);
    const idx = ids.indexOf(currentId);
    if (idx > -1 && idx < ids.length - 1) {
      this.setActiveCategory(ids[idx + 1]);
    }
  }

  goPrev(currentId) {
    const ids = this.config.categories.map((c) => c.id);
    const idx = ids.indexOf(currentId);
    if (idx > 0) {
      this.setActiveCategory(ids[idx - 1]);
    }
  }

  setSingleSelection(categoryId, payload) {
    this.state.selections[categoryId] = {
      items: {
        [payload.variantId]: {
          ...payload,
          image: payload.image,
          quantity: 1,
        },
      },
    };
    this.renderFromState();
  }

  adjustQuantity(categoryId, variantId, payload) {
    const category = this.getCategory(categoryId);
    if (!category) return;
    const isMulti =
      category.single_sku ||
      ["required_multi", "optional_multi"].includes(category.type);
    if (!isMulti) {
      this.setSingleSelection(categoryId, {
        ...payload,
        variantId,
        quantity: payload.delta > 0 ? 1 : 0,
      });
      return;
    }
    const maxTotal = category.max_quantity || 999;
    if (!this.state.selections[categoryId]) {
      this.state.selections[categoryId] = { items: {} };
    }
    const current =
      this.state.selections[categoryId].items?.[variantId]?.quantity || 0;
    const next = Math.max(0, current + payload.delta);
    const variantLimit = this.getVariantLimit(variantId, payload);
    if (variantLimit !== null) {
      if (variantLimit <= 0) {
        this.showMessage(`${payload.title || "Item"} is out of stock.`, true);
        return;
      }
      if (next > variantLimit) {
        this.showMessage(
          `Only ${variantLimit} available for ${payload.title || "this item"}.`,
          true
        );
        return;
      }
    }
    const total = category.single_sku
      ? next
      : this.getCategoryTotal(categoryId) - current + next;
    if (total > maxTotal) {
      this.showMessage(
        `Max ${maxTotal} items allowed in ${category.title}.`,
        true
      );
      return;
    }
    if (!this.state.selections[categoryId].items) {
      this.state.selections[categoryId].items = {};
    }
    if (next === 0) {
      delete this.state.selections[categoryId].items[variantId];
    } else {
      if (category.single_sku) {
        this.state.selections[categoryId].items = {};
      }
      this.state.selections[categoryId].items[variantId] = {
        ...payload,
        variantId,
        quantity: next,
      };
    }
    this.renderFromState();
  }

  getCategory(id) {
    return this.config.categories.find((c) => c.id === id);
  }

  getInventoryLimit(payload = {}) {
    const managed =
      payload.inventoryManaged ??
      payload.inventory_management ??
      "";
    const policy =
      payload.inventoryPolicy ??
      payload.inventory_policy ??
      "";
    const qtyRaw =
      payload.inventoryQty ??
      payload.inventory_quantity ??
      null;
    if (!managed) return null;
    if (policy === "continue") return null;
    const qty = Number(qtyRaw);
    if (!Number.isFinite(qty)) return null;
    return Math.max(0, qty);
  }

  getVariantLimit(variantId, payload = {}) {
    const direct = this.getInventoryLimit(payload);
    if (direct !== null) return direct;
    const product = this.variantLookup.get(String(variantId))?.product;
    if (!product) return null;
    return this.getInventoryLimit(product);
  }

  getCategoryTotal(id) {
    const selection = this.state.selections[id];
    if (!selection || !selection.items) return 0;
    return Object.values(selection.items).reduce(
      (sum, item) => sum + (item.quantity || 0),
      0
    );
  }

  categoryValid(id) {
    const category = this.getCategory(id);
    if (!category) return false;
    const total = this.getCategoryTotal(id);
    if (category.type === "required_single") {
      if (category.single_sku) {
        return total > 0;
      }
      return total === 1;
    }
    if (category.type === "required_multi") return total > 0;
    if (category.type === "optional_single") return total <= 1;
    if (category.type === "optional_multi") return total >= 0 && total <= category.max_quantity;
    return true;
  }

  allRequiredValid() {
    return this.config.categories
      .filter((c) => c.required)
      .every((c) => this.categoryValid(c.id));
  }

  renderFromState() {
    this.normalizeSingleSkuSelections();
    this.updateCards();
    this.updateNav();
    this.updateSummary();
    this.updateNextButtons();
    this.updateAddButton();
    this.renderModalTable();
    this.persistState();
  }

  updateCards() {
    this.root.querySelectorAll("[data-pc-category]").forEach((categoryEl) => {
      const categoryId = categoryEl.getAttribute("data-pc-category");
      const selection = this.state.selections[categoryId]?.items || {};
      categoryEl.querySelectorAll("[data-pc-card]").forEach((card) => {
        const variantId = card.getAttribute("data-variant-id");
        const qtyVal = selection[variantId]?.quantity || 0;
        card.classList.toggle("is-selected", qtyVal > 0);
        const qtyBadge = card.querySelector("[data-pc-qty-value]");
        if (qtyBadge) qtyBadge.textContent = qtyVal;
      });
    });
  }

  normalizeSingleSkuSelections() {
    this.config.categories.forEach((cat) => {
      if (!cat.single_sku) return;
      const selection = this.state.selections[cat.id];
      const items = selection?.items;
      if (!items) return;
      const entries = Object.entries(items);
      if (entries.length <= 1) return;
      let bestIndex = 0;
      let bestQty = entries[0][1]?.quantity || 0;
      entries.forEach(([, item], idx) => {
        const qty = item?.quantity || 0;
        if (qty > bestQty || (qty === bestQty && idx > bestIndex)) {
          bestIndex = idx;
          bestQty = qty;
        }
      });
      const [keepKey, keepItem] = entries[bestIndex];
      this.state.selections[cat.id].items = { [keepKey]: keepItem };
    });
  }

  updateNav() {
    this.root.querySelectorAll("[data-pc-nav]").forEach((nav) => {
      const categoryId = nav.getAttribute("data-pc-nav");
      const category = this.getCategory(categoryId);
      const selection = this.state.selections[categoryId]?.items || {};
      const total = Object.values(selection).reduce(
        (sum, item) => sum + (item.quantity || 0),
        0
      );
      const firstItem = Object.values(selection)[0];
      const meta = nav.querySelector("[data-pc-nav-meta]");
      const badge = nav.querySelector(".pc-builder__badge");
      if (meta) {
        meta.innerHTML = "";
        if (firstItem) {
          const label = document.createElement("span");
          label.className = "pc-builder__selected-label";
          const more =
            Object.values(selection).length > 1
              ? ` (+${Object.values(selection).length - 1} more)`
              : "";
          const qtyTxt = firstItem.quantity > 1 ? ` x${firstItem.quantity}` : "";
          label.textContent = `- ${firstItem.title}${qtyTxt}${more}`;
          meta.appendChild(label);
        } else {
          meta.textContent = "Not selected";
        }
      }
      nav.classList.remove("is-complete", "is-missing");
      if (this.categoryValid(categoryId) && total > 0) {
        nav.classList.add("is-complete");
        if (badge && category.required) {
          badge.textContent = "Selected";
          badge.classList.add("pc-builder__badge--optional");
        }
      } else if (category.required) {
        nav.classList.add("is-missing");
        if (badge) {
          badge.textContent = "Required";
          badge.classList.remove("pc-builder__badge--optional");
        }
      }
    });
  }

  updateSummary() {
    let subtotal = 0;
    let selectedItemCount = 0;
    this.config.categories.forEach((cat) => {
      const selection = this.state.selections[cat.id]?.items || {};
      Object.values(selection).forEach((item) => {
        const quantity = item.quantity || 0;
        subtotal += item.price * quantity;
        selectedItemCount += quantity;
      });
    });
    const list = this.summaryList;
    if (list) {
      list.innerHTML = "";
      this.config.categories.forEach((cat) => {
        const itemEl = document.createElement("li");
        itemEl.className = "pc-builder__summary-item";
        const selection = this.state.selections[cat.id]?.items || {};
        if (Object.keys(selection).length === 0) {
          itemEl.innerHTML = `<strong>${cat.title}</strong><span>Not selected</span>`;
        } else {
          const fragments = [];
          Object.values(selection).forEach((item) => {
            const qtyTxt = item.quantity > 1 ? ` x${item.quantity}` : "";
            fragments.push(`${item.title}${qtyTxt}`);
          });
          itemEl.innerHTML = `<strong>${cat.title}</strong><span>${fragments.join(
            ", "
          )}</span>`;
        }
        list.appendChild(itemEl);
      });
    }
    const discount = this.calculateDiscount(subtotal);
    const originalTotal = subtotal;
    const final = Math.max(subtotal - discount, 0);
    const percentLabel =
      discount > 0
        ? this.config.discount.type === "percentage"
          ? `${this.config.discount.amount}%`
          : originalTotal > 0
            ? `${Math.round((discount / originalTotal) * 100)}%`
            : ""
        : "";
    if (this.priceNodes.subtotal) {
      this.priceNodes.subtotal.textContent = PCBuilderUtils.formatMoney(originalTotal);
    }
    if (this.priceNodes.discount) {
      this.priceNodes.discount.textContent = discount
        ? `- ${PCBuilderUtils.formatMoney(discount)}`
        : PCBuilderUtils.formatMoney(0);
    }
    if (this.priceNodes.final) {
      this.priceNodes.final.textContent = PCBuilderUtils.formatMoney(final);
    }
    const selectedCountText = `${selectedItemCount} ${
      selectedItemCount === 1 ? "item" : "items"
    }`;
    if (this.priceNodes.selectedCount) {
      this.priceNodes.selectedCount.textContent = selectedCountText;
    }
    if (this.modalPrice.subtotal) {
      this.modalPrice.subtotal.textContent = PCBuilderUtils.formatMoney(originalTotal);
    }
    if (this.modalPrice.final) {
      this.modalPrice.final.textContent = PCBuilderUtils.formatMoney(final);
    }
    if (this.modalPrice.selectedCount) {
      this.modalPrice.selectedCount.textContent = selectedCountText;
    }
    if (this.priceNodes.discountBadge) {
      const badge = this.priceNodes.discountBadge;
      if (discount > 0) {
        const percent = this.config.discount.type === "percentage"
          ? `${this.config.discount.amount}%`
          : PCBuilderUtils.formatMoney(this.config.discount.amount * 100);
        badge.textContent = `Save ${percent}`;
        badge.style.display = "inline-flex";
      } else {
        badge.style.display = "none";
      }
    }
    if (this.modalPrice.discountBadge) {
      if (percentLabel) {
        this.modalPrice.discountBadge.textContent = `Save ${percentLabel}`;
        this.modalPrice.discountBadge.style.display = "inline-flex";
      } else {
        this.modalPrice.discountBadge.style.display = "none";
      }
    }
    if (this.builderStatus) {
      const allInStock = this.isAllInStock();
      this.builderStatus.classList.toggle("is-warning", !allInStock);
      this.builderStatus.textContent = allInStock ? "IN STOCK" : "Check stock";
    }
    const discountEnabled = this.discountAvailable();
    this.builderDiscountBadges.forEach((badge) => {
      badge.style.display = discountEnabled ? "inline-flex" : "none";
    });
  }

  renderModalTable() {
    if (!this.summaryGrid) {
      return;
    }
    this.summaryGrid.innerHTML = "";
    this.config.categories.forEach((cat) => {
      const selection = this.state.selections[cat.id]?.items || {};
      if (Object.keys(selection).length === 0) {
        return;
      }
      const card = document.createElement("div");
      card.className = "pc-builder__modal-item";
      const itemsHtml = Object.values(selection)
        .map((item) => {
          const skuLabel = this.config.sku_label || "SKU";
          const skuLine = item.sku
            ? `<p class="pc-builder__modal-sku">${skuLabel}: ${item.sku}</p>`
            : "";
          const imageTag = item.image
            ? `<div class="pc-builder__modal-thumb"><img src="${item.image}" alt="${item.title}"></div>`
            : "";
          const quantity = item.quantity || 0;
          return `<div class="pc-builder__modal-line">
              ${imageTag}
              <div class="pc-builder__modal-text">
                <p class="pc-builder__modal-title" title="${item.title}"><span class="pc-builder__qty-multiplier">${quantity} ×</span> ${item.title}</p>
                ${skuLine}
                <p class="pc-builder__modal-price">${PCBuilderUtils.formatMoney(
                  item.price * quantity
                )}</p>
              </div>
            </div>`;
        })
        .join("");
      card.innerHTML = `<div class="pc-builder__modal-item-head">${cat.title}</div><div class="pc-builder__modal-item-body">${itemsHtml}<div class="pc-builder__modal-actions"><button class="pc-builder__btn pc-builder__btn--ghost" data-pc-row-edit data-cat-id="${cat.id}">Edit</button></div></div>`;
      this.summaryGrid.appendChild(card);
    });
  }

  updateNextButtons() {
    this.root.querySelectorAll("[data-pc-category]").forEach((catEl) => {
      const id = catEl.getAttribute("data-pc-category");
      const isValid = this.categoryValid(id);
      const next = catEl.querySelector("[data-pc-next]");
      if (next) {
        next.disabled = !isValid && this.getCategory(id)?.required;
      }
    });
  }

  updateAddButton() {
    const enabled = this.allRequiredValid() && this.isAllInStock();
    if (this.buttons.addToCart) {
      this.buttons.addToCart.disabled = !enabled;
    }
  }

  isAllInStock() {
    const categories = this.config.categories.filter((c) => {
      const selection = this.state.selections[c.id]?.items || {};
      return Object.keys(selection).length > 0 || c.required;
    });
    return categories.every((cat) => {
      const selection = this.state.selections[cat.id]?.items || {};
      return Object.values(selection).every((item) => item.available !== false);
    });
  }

  calculateDiscount(subtotal) {
    if (
      !this.discountAvailable() ||
      !this.allRequiredValid() ||
      subtotal <= 0
    ) {
      return 0;
    }
    if (this.config.discount.type === "percentage") {
      return Math.round((subtotal * this.config.discount.amount) / 100);
    }
    return Math.min(subtotal, Math.round(this.config.discount.amount * 100));
  }

  discountAvailable() {
    if (!this.config.discount?.enabled) return false;
    if (this.config.discount.type !== "percentage") return true;
    return Number(this.config.discount.variant_id || 0) > 0;
  }

  showMessage(message, persist = false) {
    if (!this.messageEl) return;
    this.messageEl.textContent = message;
    this.messageEl.classList.add("is-visible");
    if (!persist) {
      setTimeout(() => this.messageEl.classList.remove("is-visible"), 3000);
    }
  }

  toggleModal(open) {
    if (!this.modal) return;
    this.modal.classList.toggle("is-open", open);
  }

  hydrateFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const buildParam = params.get("build");
    if (buildParam) {
      const decoded = PCBuilderUtils.base64Decode(buildParam);
      if (decoded) {
        try {
          const data = JSON.parse(decoded);
          this.applyHydratedState(data);
        } catch (e) {
          console.error("Invalid build data");
        }
      }
    }
    const edit = params.get("edit");
    if (edit) {
      this.hydrateFromCart(edit);
    }
    const cat = params.get("cat");
    if (cat && this.getCategory(cat)) {
      this.setActiveCategory(cat);
    }
  }

  applyHydratedState(payload) {
    if (!payload || !payload.selections) return;
    Object.keys(payload.selections).forEach((catId) => {
      const category = this.getCategory(catId);
      if (!category) return;
      const items = payload.selections[catId];
      Object.keys(items).forEach((variantId) => {
        const entry = items[variantId];
        const lookup = this.variantLookup.get(String(variantId));
        if (!lookup || lookup.categoryId !== catId) return;
        const maxTotal = category.max_quantity || 999;
        const variantLimit = this.getVariantLimit(variantId, lookup?.product || {});
        const maxAllowed =
          variantLimit !== null ? Math.min(maxTotal, variantLimit) : maxTotal;
        const safeQty = Math.min(entry.quantity || 0, maxAllowed);
        if (safeQty <= 0) return;
        if (!this.state.selections[catId]) {
          this.state.selections[catId] = { items: {} };
        }
        this.state.selections[catId].items[variantId] = {
          variantId,
          productId: lookup.product.product_id || lookup.product.id,
          title: lookup.product.title,
          sku: lookup.product.sku || "",
          price: lookup.product.price,
          compare: lookup.product.compare_at_price || 0,
          available: lookup.product.available,
          image: lookup.product.image,
          inventoryQty: lookup.product.inventory_quantity ?? null,
          inventoryPolicy: lookup.product.inventory_policy ?? "",
          inventoryManaged: lookup.product.inventory_management ?? "",
          quantity: safeQty,
        };
      });
    });
  }

  applyDefaultSelections() {
    this.config.categories.forEach((cat) => {
      if (!cat.required) return;
      const existing = this.state.selections[cat.id];
      if (existing && Object.keys(existing.items || {}).length > 0) return;
      const productsSorted = [...cat.products].sort(
        (a, b) => (a.price || 0) - (b.price || 0)
      );
      const availableProduct = productsSorted.find((p) => p.available !== false);
      if (!availableProduct) return;
      this.state.selections[cat.id] = {
        items: {
          [availableProduct.variant_id]: {
            variantId: availableProduct.variant_id,
            productId: availableProduct.product_id || availableProduct.id,
            title: availableProduct.title,
            sku: availableProduct.sku || "",
            price: availableProduct.price,
            compare: availableProduct.compare_at_price || 0,
            available: availableProduct.available,
            image: availableProduct.image,
            quantity: 1,
          },
        },
      };
    });
  }

  persistState() {
    try {
      const data = {
        selections: this.state.selections,
        bundleId: this.bundleId,
        bundleNumber: this.bundleNumber,
      };
      localStorage.setItem(`pcbuilder_${this.builderKey}`, JSON.stringify(data));
    } catch (e) {
      /* ignore */
    }
  }

  hydrateFromStorage() {
    try {
      const raw = localStorage.getItem(`pcbuilder_${this.builderKey}`);
      if (!raw) return;
      const data = JSON.parse(raw);
      if (data?.bundleId) this.bundleId = data.bundleId;
      if (data?.bundleNumber) this.bundleNumber = data.bundleNumber;
      if (!this.bundleNumber && this.bundleId) {
        this.bundleNumber = this.generateBundleNumber(this.bundleId);
      }
      this.applyHydratedState({ selections: data?.selections || {} });
    } catch (e) {
      /* ignore */
    }
  }

  async hydrateFromCart(bundleId) {
    try {
      const res = await fetch("/cart.js");
      const cart = await res.json();
      const targetItems = cart.items.filter(
        (item) =>
          item.properties &&
          item.properties._bundle_id &&
          item.properties._bundle_id === bundleId
      );
      if (!targetItems.length) return;
      const grouped = {};
      targetItems.forEach((item) => {
        const lookup = this.variantLookup.get(String(item.variant_id));
        const catId = lookup?.categoryId;
        if (!catId) return;
        if (!grouped[catId]) grouped[catId] = {};
        grouped[catId][item.variant_id] = {
          quantity: item.quantity,
        };
      });
      this.state.selections = {};
      this.applyHydratedState({ selections: grouped });
      this.renderFromState();
      this.bundleId = bundleId;
      const storedNumber =
        targetItems[0]?.properties?._bundle_number ||
        targetItems[0]?.properties?.["Bundle #"];
      this.bundleNumber =
        storedNumber || this.generateBundleNumber(this.bundleId);
      this.showMessage("Loaded build from cart.", true);
    } catch (e) {
      console.error("Unable to load bundle", e);
    }
  }

  encodeShareState() {
    const payload = { selections: {} };
    Object.keys(this.state.selections).forEach((catId) => {
      const items = this.state.selections[catId]?.items || {};
      if (Object.keys(items).length === 0) return;
      payload.selections[catId] = {};
      Object.values(items).forEach((item) => {
        payload.selections[catId][item.variantId] = {
          quantity: item.quantity,
        };
      });
    });
    return PCBuilderUtils.base64Encode(JSON.stringify(payload));
  }

  updateShareLink() {
    const share = this.encodeShareState();
    const url = new URL(window.location.href);
    if (share) url.searchParams.set("build", share);
    if (this.shareInput) {
      this.shareInput.value = url.toString();
    }
    return url.toString();
  }

  async copyShareLink() {
    const shareUrl = this.updateShareLink();
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      this.showMessage("Shareable link copied.");
    } catch (e) {
      this.showMessage("Copy failed, select the link manually.", true);
    }
  }

  async handleAddToCart() {
    if (!this.allRequiredValid()) {
      this.showMessage("Complete all required categories first.", true);
      return;
    }
    if (!this.isEditing) {
      this.resetBundleIdentity();
    }
    const selections = this.state.selections;
    const payloadItems = [];
    const builderUrl = new URL(window.location.href);
    builderUrl.searchParams.delete("edit");
    builderUrl.searchParams.delete("build");
    builderUrl.searchParams.delete("cat");
    builderUrl.searchParams.delete("variant");
    const currentUrl = builderUrl.toString();
    const discountEnabled = Boolean(this.config.discount?.enabled);
    const discountVariantId = discountEnabled
      ? Number(this.config.discount?.variant_id || 0)
      : 0;
    const discountVariantValue = String(discountVariantId || 0);
    const discountActivatorEnabled = discountEnabled && discountVariantId > 0;
    const discountEnabledValue = discountActivatorEnabled ? "1" : "0";
    for (const cat of this.config.categories) {
      const items = selections[cat.id]?.items || {};
      Object.values(items).forEach((item) => {
        payloadItems.push({
          id: Number(item.variantId),
          quantity: item.quantity,
          properties: {
            _bundle_id: this.bundleId,
            _bundle_number: this.bundleNumber,
            _pc_builder: "1",
            _pc_builder_discount_enabled: discountEnabledValue,
            _pc_builder_discount_variant: discountVariantValue,
            _builder_name: this.config.builder_name,
            _builder_url: currentUrl,
            _category_id: cat.id,
            _category_name: cat.title,
            "Bundle name": this.config.builder_name,
          },
        });
      });
    }
    const hasSelections = payloadItems.length > 0;
    if (hasSelections && discountActivatorEnabled) {
      payloadItems.push({
        id: discountVariantId,
        quantity: 1,
        properties: {
          _bundle_id: this.bundleId,
          _bundle_number: this.bundleNumber,
          _pc_builder: "1",
          _pc_builder_discount: "1",
          _pc_builder_discount_enabled: "1",
          _pc_builder_discount_variant: discountVariantValue,
          _builder_name: this.config.builder_name,
          _builder_url: currentUrl,
          "Bundle name": this.config.builder_name,
        },
      });
    }
    if (!payloadItems.length) {
      this.showMessage("Please choose at least one component.", true);
      return;
    }
    const stockOk = await this.validateStock(payloadItems);
    if (!stockOk) return;
    this.buttons.addToCart.disabled = true;
    this.showMessage("Adding to cart...");
    if (new URLSearchParams(window.location.search).get("edit")) {
      await this.removeExistingBundle(this.bundleId);
    }
    fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items: payloadItems }),
    })
      .then(async (res) => {
        if (!res.ok) {
          const err = await res.text();
          throw new Error(err || "Add to cart error");
        }
        return res.json();
      })
      .then(() => {
        window.location.href = "/cart";
      })
      .catch(() => {
        this.showMessage("Unable to add to cart. Try again.", true);
        this.buttons.addToCart.disabled = false;
      });
  }

  async removeExistingBundle(bundleId) {
    try {
      const res = await fetch("/cart.js");
      const cart = await res.json();
      const targetKeys = cart.items
        .filter((item) => item.properties?._bundle_id === bundleId)
        .map((item) => item.key);
      if (!targetKeys.length) return;
      const updates = {};
      targetKeys.forEach((key) => {
        updates[key] = 0;
      });
      await fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ updates }),
      });
    } catch (e) {
      console.error("Failed to clear bundle before updating", e);
    }
  }

  async validateStock(items) {
    for (const item of items) {
      const lookup = this.variantLookup.get(String(item.id));
      const limit = this.getVariantLimit(item.id, lookup?.product || {});
      if (limit !== null && item.quantity > limit) {
        this.showMessage(
          `Only ${limit} available for ${lookup?.product?.title || "this item"}.`,
          true
        );
        this.buttons.addToCart.disabled = true;
        return false;
      }
      if (lookup && lookup.product && lookup.product.available === false) {
        this.showMessage(
          `${lookup.product.title || "Item"} is no longer available.`,
          true
        );
        this.buttons.addToCart.disabled = true;
        return false;
      }
      try {
        const res = await fetch(`/variants/${item.id}.json`);
        if (res.ok) {
          const data = await res.json();
          if (data?.variant && data.variant.available === false) {
            this.showMessage(
              `${data.variant?.name || "Item"} is no longer available.`,
              true
            );
            this.buttons.addToCart.disabled = true;
            return false;
          }
          const apiLimit = this.getInventoryLimit(data?.variant || {});
          if (apiLimit !== null && item.quantity > apiLimit) {
            this.showMessage(
              `Only ${apiLimit} available for ${data.variant?.name || "this item"}.`,
              true
            );
            this.buttons.addToCart.disabled = true;
            return false;
          }
        }
      } catch (e) {
      }
    }
    return true;
  }

  isPreviewTheme() {
    const params = new URLSearchParams(window.location.search);
    if (params.has("preview_theme_id") || params.has("pb")) {
      return true;
    }
    const role = window.Shopify?.theme?.role;
    return role && role !== "main";
  }

  async refreshPreviewAvailability() {
    const shouldRefresh =
      this.isPreviewTheme() || this.allProductsUnavailable();
    if (!shouldRefresh) return;
    const itemsToCheck = [];
    this.config.categories.forEach((cat) => {
      cat.products.forEach((product) => {
        if (product.available !== true) {
          itemsToCheck.push({ catId: cat.id, product });
        }
      });
    });
    if (!itemsToCheck.length) return;
    const results = await Promise.all(
      itemsToCheck.map(async ({ catId, product }) => {
        try {
          const res = await fetch(`/variants/${product.variant_id}.json`);
          if (!res.ok) return null;
          const data = await res.json();
          return {
            catId,
            product,
            available: Boolean(data?.variant?.available),
          };
        } catch (e) {
          return null;
        }
      })
    );
    const restored = results.filter((item) => item?.available);
    if (!restored.length) return;
    restored.forEach(({ catId, product }) => {
      product.available = true;
      const lookup = this.variantLookup.get(String(product.variant_id));
      if (lookup?.product) {
        lookup.product.available = true;
      }
      const card = this.root.querySelector(
        `[data-pc-category="${catId}"] [data-variant-id="${product.variant_id}"]`
      );
      if (card) {
        card.dataset.available = "true";
        card.classList.remove("is-disabled");
        card.querySelector(".pc-builder-card__stock")?.remove();
      }
      const selection =
        this.state.selections[catId]?.items?.[product.variant_id];
      if (selection) selection.available = true;
    });
    this.applyDefaultSelections();
    this.renderFromState();
    this.updateShareLink();
  }

  allProductsUnavailable() {
    const products = [];
    this.config.categories.forEach((cat) => {
      cat.products.forEach((product) => products.push(product));
    });
    if (!products.length) return false;
    return products.every((p) => p.available !== true);
  }
}

const PCBGroupCart = {
  escapeHtml(value) {
    return String(value ?? "").replace(/[&<>"']/g, (char) => {
      switch (char) {
        case "&":
          return "&amp;";
        case "<":
          return "&lt;";
        case ">":
          return "&gt;";
        case '"':
          return "&quot;";
        case "'":
          return "&#39;";
        default:
          return char;
      }
    });
  },
  buildEditHref(url, bundleId, params = {}) {
    if (!url || url === "#") return "#";
    try {
      const resolved = new URL(url, window.location.origin);
      if (!["http:", "https:"].includes(resolved.protocol)) return "#";
      if (resolved.origin !== window.location.origin) return "#";
      resolved.searchParams.set("edit", bundleId);
      Object.entries(params || {}).forEach(([key, value]) => {
        if (value === undefined || value === null || value === "") return;
        resolved.searchParams.set(key, value);
      });
      return resolved.toString();
    } catch (e) {
      const sep = url.includes("?") ? "&" : "?";
      const entries = Object.entries(params || {}).filter(
        ([, value]) => value !== undefined && value !== null && value !== ""
      );
      const extra = entries
        .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        .join("&");
      return `${url}${sep}edit=${encodeURIComponent(bundleId)}${
        extra ? `&${extra}` : ""
      }`;
    }
  },
  init() {
    this.initContainer(document.querySelector("[data-pc-cart-groups]"), {
      rootSelector: "#main-cart-items",
      rootClass: "pc-builder-cart--grouped",
    });
    this.initContainer(document.querySelector("[data-pc-mini-cart-groups]"), {
      rootSelector: "#minicart-form",
      rootClass: "pc-builder-minicart--grouped",
    });
  },
  initContainer(container, { rootSelector, rootClass } = {}) {
    if (!container) return;
    if (container._pcBuilderGroupsInitialized) return;
    container._pcBuilderGroupsInitialized = true;
    let cartData;
    try {
      cartData = JSON.parse(container.dataset.cartPayload || "{}");
    } catch (e) {
      return;
    }
    if (!cartData.items || !cartData.items.length) return;
    const groups = {};
    cartData.items.forEach((item) => {
      if (isPCBuilderDiscountItem(item)) return;
      const bundleId = item.properties?._bundle_id;
      if (!bundleId) return;
      if (!groups[bundleId]) {
        groups[bundleId] = {
          name: item.properties?._builder_name || "Custom PC build",
          url: item.properties?._builder_url || "#",
          number:
            item.properties?._bundle_number ||
            item.properties?.["Bundle #"] ||
            "",
          items: [],
          lineKeys: [],
        };
      }
      if (
        !groups[bundleId].number &&
        (item.properties?._bundle_number || item.properties?.["Bundle #"])
      ) {
        groups[bundleId].number =
          item.properties?._bundle_number || item.properties?.["Bundle #"];
      }
      groups[bundleId].items.push({
        categoryId: item.properties?._category_id || "",
        category: item.properties?._category_name || "Component",
        title: item.title,
        quantity: item.quantity,
        price: item.final_line_price,
        unit: item.final_price,
        line: item.key,
        url: item.url,
      });
      groups[bundleId].lineKeys.push(item.key);
    });
    const groupEntries = Object.entries(groups);
    if (!groupEntries.length) return;
    const wrapper = document.createElement("div");
    wrapper.className = "pc-builder__group-grid pc-builder__panel";
    groupEntries.forEach(([id, group]) => {
      const card = document.createElement("div");
      card.className = "pc-builder__group-card";
      const total = group.items.reduce((sum, i) => sum + i.price, 0);
      const editHref = this.buildEditHref(group.url, id);
      const bundleLabelRaw =
        group.number || (id ? `#${String(id).slice(-6)}` : "");
      const bundleLabel = bundleLabelRaw
        ? bundleLabelRaw.startsWith("#")
          ? bundleLabelRaw
          : `#${bundleLabelRaw}`
        : "";
      const componentCount = group.items.length;
      const totalQty = group.items.reduce(
        (sum, item) => sum + (item.quantity || 0),
        0
      );
      const subtitleParts = [];
      if (bundleLabel) subtitleParts.push(`Bundle ${bundleLabel}`);
      if (componentCount) subtitleParts.push(`${componentCount} components`);
      if (totalQty && totalQty !== componentCount) {
        subtitleParts.push(`${totalQty} items`);
      }
      const subtitle = subtitleParts.join(" • ");
      card.innerHTML = `
        <div class="pc-builder__group-head">
          <div class="pc-builder__group-title">
            <div class="pc-builder__pill pc-builder__cart-badge">PC Builder</div>
            <p class="pc-builder__group-name">${this.escapeHtml(
              group.name || "Custom PC build"
            )}</p>
            ${
              subtitle
                ? `<p class="pc-builder__group-subtitle">${this.escapeHtml(
                    subtitle
                  )}</p>`
                : ""
            }
          </div>
          <div class="pc-builder__group-total">${PCBuilderUtils.formatMoney(
            total
          )}</div>
        </div>
        <div class="pc-builder__group-list">
          ${group.items
            .map(
              (i) =>
                `<div class="pc-builder__group-row">
                  <div>
                    <span>${this.escapeHtml(i.category)}</span>
                    <strong>${this.escapeHtml(i.title)}</strong>
                  </div>
                  <div class="text-right">
                    <span>Qty ${i.quantity}</span>
                    <strong>${PCBuilderUtils.formatMoney(i.unit)}</strong>
                    ${
                      i.categoryId
                        ? `<a class="pc-builder__group-row-edit" href="${this.buildEditHref(
                            group.url,
                            id,
                            { cat: i.categoryId }
                          )}">Edit</a>`
                        : ""
                    }
                  </div>
                </div>`
            )
            .join("")}
        </div>
        <p class="pc-builder__bundle-note">This build is managed as a single bundle. Use “Edit build” to change components.</p>
        <div class="pc-builder__group-actions">
          <a class="pc-builder__btn pc-builder__btn--ghost" href="${editHref}">Edit build</a>
          <button type="button" class="pc-builder__btn pc-builder__btn--primary" data-pc-remove-bundle="${id}" data-pc-edit-href="${this.escapeHtml(editHref)}">Remove bundle</button>
        </div>
      `;
      wrapper.appendChild(card);
    });
    container.appendChild(wrapper);
    if (rootSelector && rootClass) {
      document.querySelector(rootSelector)?.classList.add(rootClass);
    }
    container.addEventListener("click", async (e) => {
      const btn = e.target.closest("[data-pc-remove-bundle]");
      if (!btn) return;
      const bundleId = btn.dataset.pcRemoveBundle;
      if (!bundleId) return;
      const editHref = btn.dataset.pcEditHref;
      const action =
        typeof CartUtils !== "undefined" &&
        typeof CartUtils.confirmBundleRemoval === "function"
          ? await CartUtils.confirmBundleRemoval({ bundleLabel: "this bundle", editHref })
          : await PCBuilderUtils.confirmBundleAction({ bundleLabel: "this bundle", editHref });
      if (action === "edit" && editHref) {
        window.location.href = editHref;
        return;
      }
      if (action !== "delete") return;
      this.removeBundle(bundleId);
    });
  },
  async removeBundle(bundleId) {
    try {
      const res = await fetch("/cart.js");
      const cart = await res.json();
      const updates = {};
      cart.items.forEach((item) => {
        if (item.properties?._bundle_id === bundleId) {
          updates[item.key] = 0;
        }
      });
      await fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ updates }),
      });
      window.location.reload();
    } catch (e) {
      console.error("Failed to remove bundle", e);
    }
  },
};

const PCBCartCheckoutCleanup = {
  initialized: false,
  lastSubmitter: null,
  lastForm: null,
  async stripBundleProperties() {
    try {
      const res = await fetch("/cart.js");
      if (!res.ok) return;
      const cart = await res.json();
      const targets = (cart.items || []).filter(
        (item) =>
          !isPCBuilderDiscountItem(item) &&
          (item.properties?._bundle_id || item.properties?._pc_builder)
      );
      for (const item of targets) {
        const bundleName =
          item.properties?.["Bundle name"] ||
          item.properties?._builder_name ||
          "PC Builder bundle";
        const propKeys = Object.keys(item.properties || {});
        if (propKeys.length === 1 && propKeys[0] === "Bundle name") {
          continue;
        }
        await fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id: item.key,
            quantity: item.quantity,
            properties: { "Bundle name": bundleName },
          }),
        });
      }
    } catch (e) {
      console.warn("[PC Builder] Unable to sanitize bundle properties", e);
    }
  },
  async ensurePcBuilderDiscounts() {
    try {
      if (typeof PCBuilderDiscountGuard !== "undefined") {
        await PCBuilderDiscountGuard.sync();
        return;
      }
      const cartItems = document.querySelector("cart-items");
      if (
        cartItems &&
        typeof cartItems.ensurePcBuilderDiscounts === "function"
      ) {
        await cartItems.ensurePcBuilderDiscounts();
        return;
      }
      const cartNotification = document.querySelector("cart-notification");
      if (
        cartNotification &&
        typeof cartNotification.ensurePcBuilderDiscounts === "function"
      ) {
        await cartNotification.ensurePcBuilderDiscounts();
      }
    } catch (e) {
      console.warn("[PC Builder] Unable to enforce bundle discounts", e);
    }
  },
  async handleCheckout(form, submitter) {
    if (form.dataset.pcCheckoutSanitized === "true") return;
    form.dataset.pcCheckoutSanitized = "true";
    await this.ensurePcBuilderDiscounts();
    await this.stripBundleProperties();
    this.resumeCheckout(form, submitter);
  },
  buildCheckoutFallback(form) {
    const hidden = document.createElement("input");
    hidden.type = "hidden";
    hidden.name = "checkout";
    hidden.value = "checkout";
    form.appendChild(hidden);
    return hidden;
  },
  resumeCheckout(form, submitter) {
    if (form.requestSubmit && submitter) {
      form.requestSubmit(submitter);
      return;
    }
    const hidden = this.buildCheckoutFallback(form);
    form.submit();
    hidden.remove();
  },
  init() {
    if (this.initialized) return;
    this.initialized = true;

    document.addEventListener("click", (event) => {
      const checkoutBtn = event.target.closest('[name="checkout"]');
      if (!checkoutBtn) return;
      const form = checkoutBtn.form || document.getElementById(checkoutBtn.getAttribute("form"));
      if (!form || !(form instanceof HTMLFormElement)) return;
      if (!["cart", "minicart"].includes(form.id)) return;
      form.dataset.pcCheckoutIntent = "true";
      this.lastSubmitter = checkoutBtn;
      this.lastForm = form;
      event.preventDefault();
      this.handleCheckout(form, checkoutBtn);
    });

    document.addEventListener(
      "submit",
      async (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement)) return;
        if (!["cart", "minicart"].includes(form.id)) return;
        const submitter =
          event.submitter || (this.lastForm === form ? this.lastSubmitter : null);
        const isCheckout =
          submitter?.name === "checkout" ||
          submitter?.closest(".additional-checkout-buttons") ||
          form.dataset.pcCheckoutIntent === "true";
        if (!isCheckout) return;
        if (form.dataset.pcCheckoutSanitized === "true") return;
        event.preventDefault();
        await this.handleCheckout(form, submitter);
      },
      true
    );
  },
};

const initPCBuilderSections = (scope = document) => {
  scope.querySelectorAll("[data-pc-builder]").forEach((section) => {
    if (section._pcBuilderInitialized) return;
    section._pcBuilderInitialized = true;
    try {
      new PCBuilder(section);
    } catch (e) {
      console.error("[PC Builder] Failed to init section", e);
    }
  });
  try {
    PCBGroupCart.init();
    PCBCartCheckoutCleanup.init();
  } catch (e) {
    console.error("[PC Builder] Failed to init cart groups", e);
  }
};

document.addEventListener("DOMContentLoaded", () => initPCBuilderSections());
document.addEventListener("shopify:section:load", (event) =>
  initPCBuilderSections(event.target)
);

(() => {
  const selector = "[data-pc-mini-cart-groups], [data-pc-cart-groups]";
  let scheduled = false;
  const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      try {
        PCBGroupCart.init();
      } catch (e) {
        /* ignore */
      }
    });
  };

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        if (node.matches(selector) || node.querySelector(selector)) {
          schedule();
        }
      });
    }
  });

  document.addEventListener("DOMContentLoaded", () => {
    if (!document.body) return;
    observer.observe(document.body, { childList: true, subtree: true });
  });
})();
